import { z } from 'zod';

import { toSnakeCase } from '@vestigas/utils';

import {
  ANALYTICS_CHART_AGGREGATION,
  ANALYTICS_CHART_NAMES,
  ANALYTICS_CHART_TYPES,
  DELIVERY_NOTE_ANALYTICS_CHART_NAMES,
  USER_ANALYTICS_CHART_NAMES,
} from './constants';

/**
 * Zod schema for validating analytics chart query parameters.
 */
export const zodAnalyticsChartQueryParamsSchema = z.object({
  aggregation: z
    .preprocess(
      (value) => (typeof value === 'string' ? value.toLowerCase() : value),
      z.enum(ANALYTICS_CHART_AGGREGATION),
    )
    .prefault('week'),
  dateRange: z.object({
    dateFrom: z.iso.date(),
    dateTo: z.iso.date(),
  }),
  name: z.preprocess(
    (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
    z.enum(ANALYTICS_CHART_NAMES),
  ),
});

/**
 * Zod schema for validating a single data point in a chart dataset.
 */
export const zodAnalyticsChartDataPointSchema = z.object({
  date: z.iso.date(),
  value: z.number(),
});

/**
 * Zod schema for validating a chart dataset.
 */
export const zodAnalyticsChartDatasetSchema = z.object({
  data: z.union([
    z.array(zodAnalyticsChartDataPointSchema),
    z.array(z.number()),
  ]),
  label: z.string(),
});

/**
 * Zod schema for validating the analytics chart API response.
 */
export const zodAnalyticsChartResponseSchema = z.object({
  data: z.object({
    chartType: z.enum(ANALYTICS_CHART_TYPES),
    datasets: z.array(zodAnalyticsChartDatasetSchema),
    labels: z.array(z.string()),
  }),
});

/**
 * Zod schema for validating the various analytics chart API endpoints.
 */
export const zodAnalyticsChartEndpointSchema = z.union([
  z.tuple([
    z.literal('user'),
    z.literal('activity'),
    z.literal('chart'),
    z.enum(USER_ANALYTICS_CHART_NAMES),
  ]),
  z.tuple([
    z.literal('delivery_note'),
    z.literal('statistics'),
    z.literal('chart'),
    z.enum(DELIVERY_NOTE_ANALYTICS_CHART_NAMES),
  ]),
]);
